import { stripLinePrefix } from './soap-section-format.util';

const EXAM_HEADING = 'Physical Examination';
const LABS_HEADING = 'Laboratory, Imaging, and Diagnostic Test Results';

const EXAM_LABELS = /^(vitals?|general(?:\s+appearance)?|heent|neck|cv|cardiovascular|heart|respiratory|lungs|chest|gastrointestinal|abdomen|genitourinary|musculoskeletal|extremities|skin|integumentary|neuro(?:logical)?|psych(?:iatric)?|mental status)\b/i;

const EXAM_HEADING_RE = /^physical\s+exam/i;
const LABS_HEADING_RE =
  /^(laboratory,\s*imaging,\s*and\s*diagnostic\s+test\s+results|diagnostics?(?:\s*\/\s*data)?|diagnostic\s+test|laboratory\s+results|labs?\b|imaging\b|intake\s+and\s+output|assessment\s+tools)/i;
const WRAPPER_RE = /^(previous\s+results|diagnostics?(?:\s*\/\s*data)?|diagnostic\s+test\s+results|laboratory\s+results|intake\s+and\s+output|assessment\s+tools|scores)\s*:?\s*$/i;
const VITALS_HEADING_RE = /^vitals?\b/i;

/**
 * Rewrite Objective into Physical Examination (System: findings) plus a
 * bulleted Laboratory/Imaging/Results list, keeping vitals, general, exam,
 * diagnostics, and scores.
 */
export function foldObjectiveForDisplay(text: string | null | undefined): string {
  const raw = (text ?? '').replace(/\r/g, '').trim();
  if (!raw || /^n\/a$/i.test(raw)) {
    return raw;
  }

  const exam: string[] = [];
  const labs: string[] = [];
  let current: 'exam' | 'labs' | null = null;
  let pendingExamLabel: string | null = null;

  const pushUnique = (list: string[], line: string) => {
    if (!list.some((existing) => existing.toLowerCase() === line.toLowerCase())) {
      list.push(line);
    }
  };

  const add = (kind: 'exam' | 'labs', line: string, pendingLabel: string | null) => {
    let stripped = stripLinePrefix(line).trim();
    if (!stripped || WRAPPER_RE.test(stripped)) {
      return;
    }
    if (kind === 'labs') {
      if (!stripped.startsWith('- ')) {
        stripped = '- ' + stripped;
      }
      pushUnique(labs, stripped);
      return;
    }
    if (pendingLabel && !stripped.includes(':')) {
      stripped = `${pendingLabel}: ${stripped}`;
    }
    const examLine = toExamLine(stripped);
    if (examLine) {
      pushUnique(exam, examLine);
      return;
    }
    pushUnique(labs, stripped.startsWith('- ') ? stripped : '- ' + stripped);
  };

  for (const rawLine of raw.split('\n')) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      continue;
    }
    const heading = matchObjectiveHeading(trimmed);
    if (heading) {
      current = heading.kind;
      pendingExamLabel = heading.pendingLabel;
      if (heading.inline) {
        add(heading.kind, heading.inline, pendingExamLabel);
        pendingExamLabel = null;
      }
      continue;
    }
    const kind = inferKind(trimmed, current);
    add(kind, trimmed, pendingExamLabel);
    pendingExamLabel = null;
    current ??= kind;
  }

  const parts: string[] = [];
  if (exam.length) {
    parts.push([EXAM_HEADING, ...exam].join('\n'));
  }
  if (labs.length) {
    parts.push([LABS_HEADING, ...labs].join('\n'));
  }
  return parts.join('\n\n').trim() || raw;
}

function matchObjectiveHeading(line: string): { kind: 'exam' | 'labs'; inline: string; pendingLabel: string | null } | null {
  const stripped = stripLinePrefix(line).replace(/:$/, '').trim();
  const colon = stripLinePrefix(line).indexOf(':');
  let title = stripped;
  let inline = '';
  const rawStripped = stripLinePrefix(line).trim();
  const rawColon = rawStripped.indexOf(':');
  if (rawColon > 0 && rawColon < 60 && rawColon < rawStripped.length - 1) {
    title = rawStripped.slice(0, rawColon).trim();
    inline = rawStripped.slice(rawColon + 1).trim();
  } else if (rawStripped.endsWith(':')) {
    title = rawStripped.slice(0, -1).trim();
  }
  if (EXAM_HEADING_RE.test(title)) {
    return { kind: 'exam', inline, pendingLabel: null };
  }
  if (LABS_HEADING_RE.test(title) || WRAPPER_RE.test(rawStripped)) {
    return { kind: 'labs', inline, pendingLabel: null };
  }
  if (VITALS_HEADING_RE.test(title)) {
    return { kind: 'exam', inline: inline ? `Vitals: ${inline}` : '', pendingLabel: inline ? null : 'Vitals' };
  }
  if (/^general(?:\s+appearance)?$/i.test(title)) {
    return { kind: 'exam', inline: inline ? `General: ${inline}` : '', pendingLabel: inline ? null : 'General' };
  }
  return null;
}

function inferKind(line: string, current: 'exam' | 'labs' | null): 'exam' | 'labs' {
  const stripped = stripLinePrefix(line);
  const colon = stripped.indexOf(':');
  if (colon > 0 && colon < 48) {
    const label = stripped.slice(0, colon).trim();
    if (EXAM_LABELS.test(label) || VITALS_HEADING_RE.test(label) || /^general$/i.test(label)) {
      return 'exam';
    }
    return 'labs';
  }
  return current ?? 'labs';
}

function toExamLine(stripped: string): string | null {
  if (EXAM_HEADING_RE.test(stripped)) {
    return null;
  }
  const colon = stripped.indexOf(':');
  if (colon > 0 && colon < 48) {
    const label = stripped.slice(0, colon).trim();
    const content = stripped.slice(colon + 1).trim();
    if (!content) {
      return null;
    }
    if (VITALS_HEADING_RE.test(label)) {
      return `Vitals: ${content}`;
    }
    if (/^general(?:\s+appearance)?$/i.test(label)) {
      return `General: ${content}`;
    }
    if (/^neuro/i.test(label)) {
      return `Neurological: ${content}`;
    }
    if (/^psych/i.test(label)) {
      return `Psychiatric: ${content}`;
    }
    if (EXAM_LABELS.test(label)) {
      return `${label}: ${content}`;
    }
    return null;
  }
  return stripped;
}
